import {
  ActiveVariantType,
  SavedFilter,
  SavedFilters,
  DefaultFilters,
} from '../../types';
import { AxiosResponse } from 'axios';
import { defineMessages, useIntl } from 'react-intl';
import { useActiveProject } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveProject';
import { useActiveVariantType } from '../../ActiveVariantTypeContext';
import { useCurrentDefaultFilter } from '../../useCurrentDefaultFilter';
import { useDefaultFilters } from '../../DefaultFiltersContext';
import { UseHydrateOmicsExplorer } from '../../useHydrateOmicsExplorer';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks/useNotify';
import { useSavedFilters } from '../../SavedFiltersContext';
import { useSelectedFilter } from '../../SelectedFilterContext';
import { useSelectedFilterOptions } from '../../../../../src/components/common/Genomics/SelectedFilterOptionsContext';
import { useSelectedSequenceType } from '../../SequenceTypeContext';
import * as React from 'react';
import OESavedFiltersService from '@lifeomic/phc-web-toolkit/src/services/OESavedFiltersService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';

const oeSavedFiltersService = new OESavedFiltersService();

const formatVariantTypeForRequest = (variantType: ActiveVariantType) => {
  // Add "Variant" for types other than expression to fulfill our API request
  // https://bitbucket.org/lifeomic/phc-ui-api/src/9acad2f51369653246b45ce4babcb7412f009581/src/schemas/OmicsFilter.json?at=master#lines-24
  return variantType !== 'expression' ? `${variantType}Variant` : variantType;
};

const updateFiltersObject = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  filters,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  selectedSequenceType,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  selectedActiveVariantType,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  newFilterStatus,
}) => {
  // Update our saved filters from the root level so that
  // as users navigate around, they see the latest stuff
  const filtersClone = { ...filters };
  filtersClone[
    `${selectedSequenceType}${
      selectedActiveVariantType.charAt(0).toUpperCase() +
      selectedActiveVariantType.slice(1)
    }`
  ] = newFilterStatus;
  return filtersClone;
};

const pushToSavedFilters = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  savedFilters,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  selectedSequenceType,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  selectedActiveVariantType,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  newFilter,
}) => {
  const savedFiltersClone: SavedFilters = { ...savedFilters };
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  savedFiltersClone[
    `${selectedSequenceType}${
      selectedActiveVariantType.charAt(0).toUpperCase() +
      selectedActiveVariantType.slice(1)
    }`
  ]?.push(newFilter);
  return savedFiltersClone;
};

const addFilterAsDefaultFilter = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  defaultFilters,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  selectedSequenceType,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  selectedActiveVariantType,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  newFilter,
}) => {
  const defaultFiltersClone: DefaultFilters = { ...defaultFilters };
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  defaultFiltersClone[
    `${selectedSequenceType}${
      selectedActiveVariantType.charAt(0).toUpperCase() +
      selectedActiveVariantType.slice(1)
    }`
  ] = newFilter;
  return defaultFiltersClone;
};

const messages = defineMessages({
  createSuccessToast: {
    id: 'omicsExplorer.savedFilters.createSuccessToast',
    defaultMessage: 'Successfully created the saved filter',
  },
  createFailureToast: {
    id: 'omicsExplorer.savedFilters.createFailureToast',
    defaultMessage: 'An error occurred creating the filter',
  },
  putDefaultFilterSuccessToast: {
    id: 'omicsExplorer.savedFilters.putDefaultFilterSuccessToast',
    defaultMessage: 'Successfully set as the default filter',
  },
  deleteSuccessToast: {
    id: 'omicsExplorer.savedFilters.deleteSuccessToast',
    defaultMessage: 'Successfully deleted the saved filter',
  },
  deleteFailureToast: {
    id: 'omicsExplorer.savedFilters.deleteFailureToast',
    defaultMessage: 'An error occurred deleting the filter',
  },
  noIndexErrorToast: {
    id: 'omicsExplorer.savedFilters.noIndexErrorToast',
    defaultMessage: 'Uh-oh, you hit a bug with filters! Please contact us.',
  },
  updateSuccessToast: {
    id: 'omicsExplorer.savedFilters.updateSuccessToast',
    defaultMessage: 'Successfully updated the saved filter',
  },
  updateFailureToast: {
    id: 'omicsExplorer.savedFilters.updateFailureToast',
    defaultMessage: 'An error occurred updating the filter',
  },
  defaultFailureToast: {
    id: 'omicsExplorer.savedFilters.defaultFailureToast',
    defaultMessage: 'An error occurred setting the default filter',
  },
});

interface UseManagedSavedFilters {
  state: UseHydrateOmicsExplorer['state'];
}

export function useManageSavedFilters({ state }: UseManagedSavedFilters) {
  const { formatMessage } = useIntl();

  // Local hook state
  const [
    currentFilterBasedOnSequenceAndVariantType,
    setCurrentFilterBasedOnSequenceAndVariantType,
  ] = React.useState<Array<SavedFilter>>([]);

  // Hooks
  const [activeProject] = useActiveProject();
  const [addNote] = useNotify();
  const [selectedActiveVariantType] = useActiveVariantType();
  const [selectedSequenceType] = useSelectedSequenceType();
  const [selectedFilter, setSelectedFilter] = useSelectedFilter();
  const [savedFilters, setSavedFilters] = useSavedFilters();
  const [defaultFilters, setDefaultFilters] = useDefaultFilters();
  const [selectedFilterOptions, setSelectedFilterOptions] =
    useSelectedFilterOptions();
  const { currentDefaultFilter, updateDefaultFilter } = useCurrentDefaultFilter(
    {
      defaultFilters,
      selectedActiveVariantType,
      selectedSequenceType,
      hydrateState: state,
    },
  );

  // Service bits
  const [createFilter] = useService(oeSavedFiltersService.createFilter);
  const [deleteFilter] = useService(oeSavedFiltersService.deleteFilter);
  const [updateFilter] = useService(oeSavedFiltersService.updateFilter);
  const [putDefaultFilter] = useService(oeSavedFiltersService.putDefaultFilter);

  // Determine which saved filters to populate our select with
  // by grabbing the selected sequence type and selected active
  // variant type (e.g., "somaticShort") and pulling off the filters
  // from the `savedFilters` object
  React.useEffect(() => {
    if (!savedFilters) {
      return;
    }

    const filtersBasedOnSelections =
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      savedFilters[
        `${selectedSequenceType}${
          selectedActiveVariantType.charAt(0).toUpperCase() +
          selectedActiveVariantType.slice(1)
        }`
      ];

    if (!filtersBasedOnSelections) {
      return;
    }

    setCurrentFilterBasedOnSequenceAndVariantType(filtersBasedOnSelections);
  }, [savedFilters, selectedActiveVariantType, selectedSequenceType]);

  const onCreateNewFilter = async ({
    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    name,
    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    description,
    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    setAsDefaultFilter,
  }): Promise<AxiosResponse> => {
    try {
      const response = await createFilter({
        projectId: activeProject.id,
        sequenceType: selectedSequenceType,
        variantType: formatVariantTypeForRequest(
          selectedActiveVariantType,
        ) as any,
        name,
        description,
        filters: selectedFilterOptions,
      });

      // Add the newly created filter to our internal list so
      // it shows up in the dropdown as an option
      if (response && response.status === 200) {
        setCurrentFilterBasedOnSequenceAndVariantType([
          ...currentFilterBasedOnSequenceAndVariantType,
          response.data,
        ]);
        setSelectedFilter(response.data);
        setSavedFilters(
          pushToSavedFilters({
            savedFilters,
            selectedSequenceType,
            selectedActiveVariantType,
            newFilter: response.data,
          }),
        );
        addNote(formatMessage(messages.createSuccessToast), 'success');
      }

      // After the filter is created, we can then
      // set it as the default if the user so chooses
      if (!setAsDefaultFilter) {
        return response;
      }

      const setAsDefaultFilterResponse = await putDefaultFilter(
        response.data.id,
      );

      if (
        setAsDefaultFilterResponse &&
        setAsDefaultFilterResponse.status === 200
      ) {
        updateDefaultFilter(response.data);
        setDefaultFilters(
          addFilterAsDefaultFilter({
            defaultFilters,
            selectedActiveVariantType,
            selectedSequenceType,
            newFilter: response.data,
          }),
        );
        addNote(
          formatMessage(messages.putDefaultFilterSuccessToast),
          'success',
        );
      }

      return response;
    } catch (error) {
      console.error('Error creating filter', error);
      addNote(formatMessage(messages.createFailureToast), 'error');
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return null;
    }
  };

  const onDeleteFilter = async (
    filter: SavedFilter,
  ): Promise<AxiosResponse> => {
    const index = currentFilterBasedOnSequenceAndVariantType.findIndex(
      i => i.id === filter.id,
    );

    if (index < 0) {
      console.error(
        `Could not find index for ${
          filter.id
        } in ${currentFilterBasedOnSequenceAndVariantType
          .map(f => f.id)
          .join(',')}`,
      );
      addNote(formatMessage(messages.noIndexErrorToast), 'error');
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return null;
    }

    try {
      const response = await deleteFilter(filter.id);

      if (!response) {
        throw new Error('Did not receive a response when deleting a filter.');
      }

      if (response.status !== 204) {
        throw new Error(
          `Error deleting filter. Returned with HTTP status ${response.status}`,
        );
      }

      // Remove the selected filter from our internal list so
      // it doesn't show up in the dropdown anymore
      const newSavedFilters = [...currentFilterBasedOnSequenceAndVariantType];
      newSavedFilters.splice(index, 1);
      setCurrentFilterBasedOnSequenceAndVariantType(newSavedFilters);
      setSavedFilters(
        updateFiltersObject({
          filters: savedFilters,
          selectedSequenceType,
          selectedActiveVariantType,
          newFilterStatus: newSavedFilters,
        }),
      );

      if (filter.id === currentDefaultFilter.id) {
        setDefaultFilters(
          updateFiltersObject({
            filters: defaultFilters,
            selectedSequenceType,
            selectedActiveVariantType,
            newFilterStatus: null,
          }),
        );
      }

      addNote(formatMessage(messages.deleteSuccessToast), 'success');

      // If the filter we are deleting is the selected one,
      // for UI/UX reasons, we will clear out the selected filter.
      if (selectedFilter && filter.id === selectedFilter.id) {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        setSelectedFilter(null);
        setSelectedFilterOptions([]);
      }

      return response;
    } catch (error) {
      console.error('Error deleting filter', error);
      addNote(formatMessage(messages.deleteFailureToast), 'error');
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return null;
    }
  };

  const onUpdateDefaultFilter = async (filter: SavedFilter) => {
    const response = await putDefaultFilter(filter.id);

    if (response?.status !== 200) {
      addNote(formatMessage(messages.defaultFailureToast), 'error');
      console.error(
        `Setting the filter as default returned: ${response.status}`,
      );
      return filter;
    }

    updateDefaultFilter(filter);
    setDefaultFilters(
      updateFiltersObject({
        filters: defaultFilters,
        selectedActiveVariantType,
        selectedSequenceType,
        newFilterStatus: filter,
      }),
    );
    addNote(formatMessage(messages.putDefaultFilterSuccessToast), 'success');
    return filter;
  };

  const onUpdateFilter = async (
    filter: SavedFilter,
    setAsDefaultFilter: boolean,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  ): Promise<AxiosResponse> => {
    const index = currentFilterBasedOnSequenceAndVariantType.findIndex(
      i => i.id === filter.id,
    );

    const newFilter = {
      ...filter,
      filters: selectedFilterOptions,
    };

    if (index < 0) {
      console.error(
        `Could not find index for ${
          newFilter.id
        } in ${currentFilterBasedOnSequenceAndVariantType
          .map(f => f.id)
          .join(',')}`,
      );
      addNote(formatMessage(messages.noIndexErrorToast), 'error');
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return null;
    }

    try {
      const response = await updateFilter({
        id: filter.id,
        oldFilter: selectedFilter,
        newFilter,
      } as any);

      if (!response) {
        throw new Error('Did not receive a response when updating a filter.');
      }

      if (response.status !== 200) {
        throw new Error(
          `Error updating filter. Returned with HTTP status ${response.status}`,
        );
      }

      // Update the selected filter + current filter
      const currentFilterBasedOnSequenceAndVariantTypeCopy = [
        ...currentFilterBasedOnSequenceAndVariantType,
      ];
      currentFilterBasedOnSequenceAndVariantTypeCopy.splice(
        index,
        1,
        response.data,
      );
      setCurrentFilterBasedOnSequenceAndVariantType(
        currentFilterBasedOnSequenceAndVariantTypeCopy,
      );
      setSelectedFilter(response.data);
      setSavedFilters(
        updateFiltersObject({
          filters: savedFilters,
          selectedActiveVariantType,
          selectedSequenceType,
          newFilterStatus: currentFilterBasedOnSequenceAndVariantTypeCopy,
        }),
      );

      addNote(formatMessage(messages.updateSuccessToast), 'success');

      // After the filter is updated, we can then
      // set it as the default if the user so chooses
      if (!setAsDefaultFilter) {
        return response;
      }

      // This is already the default, so no need to set as default again;
      // however, we need to update our context so that other parts of the
      // UI update
      if (currentDefaultFilter?.id === newFilter?.id) {
        setDefaultFilters(
          updateFiltersObject({
            filters: defaultFilters,
            selectedActiveVariantType,
            selectedSequenceType,
            newFilterStatus: response.data,
          }),
        );
        return response;
      }

      onUpdateDefaultFilter(response.data);
    } catch (error) {
      console.error('Error updating filter', error);
      addNote(formatMessage(messages.updateFailureToast), 'error');
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return null;
    }
  };

  return {
    currentFilterBasedOnSequenceAndVariantType,
    currentDefaultFilter,
    onCreateNewFilter,
    onDeleteFilter,
    onUpdateFilter,
    onUpdateDefaultFilter,
  };
}
